export const createLitigationQuestionDraft = ({
  id = createLitigationQuestionId(),
  prompt = "",
  responseType = "keyboard",
  supplementalOptions = []
} = {}) => {
  const respType = draftResponseType(responseType);

  return {
    id,
    question: prompt,
    respType,
    respOpts: respType !== "YesNo"
      ? draftSupplementalOptions(supplementalOptions)
      : []
  };
};

export const cleanLitigationQuestionDrafts = questions => {
  if (!Array.isArray(questions)) {
    return [];
  }

  return questions.filter(
    question => String(question.question ?? "").trim() !== ""
  );
};

export const litigationQuestionDrafts = (questions, category) => {
  if (!Array.isArray(questions)) {
    return [];
  }

  return questions
    .filter(question => question.category === category)
    .map(question => createLitigationQuestionDraft(question));
};

export const buildLitigationQuestions = ({
  qualificationQuestions,
  personalInjuryQuestions,
  economicDamageQuestions
}) => [
  ...buildCategoryQuestions(qualificationQuestions, "qualification"),
  ...buildCategoryQuestions(personalInjuryQuestions, "personalInjury"),
  ...buildCategoryQuestions(economicDamageQuestions, "economicDamage")
];

const buildCategoryQuestions = (questions, category) => {
  return cleanLitigationQuestionDrafts(questions).map(question => {
    const responseType = storedResponseType(question.respType);

    return {
      id: question.id ?? createLitigationQuestionId(),
      category,
      prompt: String(question.question).trim(),
      responseType,
      supplementalOptions: responseType !== "yesNo"
        ? storedSupplementalOptions(question.respOpts)
        : []
    };
  });
};

const storedResponseType = responseType => {
  switch (responseType) {
  case "Number":
    return "number";

  case "YesNo":
    return "yesNo";

  default:
    return "keyboard";
  }
};

const draftResponseType = responseType => {
  switch (responseType) {
  case "number":
    return "Number";

  case "yesNo":
    return "YesNo";

  default:
    return "Keyboard";
  }
};

const storedSupplementalOptions = options => {
  if (!Array.isArray(options)) {
    return [];
  }

  const allowedOptions = {
    skip: "skip",
    next: "next",
    "n/a": "notApplicable",
    na: "notApplicable",
    "not applicable": "notApplicable",
    notapplicable: "notApplicable"
  };

  return [...new Set(
    options
      .map(option => allowedOptions[String(option).trim().toLowerCase()])
      .filter(Boolean)
  )];
};

const draftSupplementalOptions = options => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options.map(option => {
    switch (option) {
    case "notApplicable":
      return "n/a";

    default:
      return option;
    }
  });
};

const createLitigationQuestionId = () => {
  const browserCrypto = window.crypto;

  if (typeof browserCrypto?.randomUUID === "function") {
    return browserCrypto.randomUUID();
  }

  if (typeof browserCrypto?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    browserCrypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const value = [...bytes]
      .map(byte => byte.toString(16).padStart(2, "0"))
      .join("");

    return [
      value.slice(0, 8),
      value.slice(8, 12),
      value.slice(12, 16),
      value.slice(16, 20),
      value.slice(20)
    ].join("-");
  }

  return `question-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};
